import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, Avatar } from "@/components/common";
import CotaPendenteBanner from "@/components/CotaPendenteBanner";
import { brl, pct, num, plural } from "@/lib/format";
import {
  DollarSign, Target, TrendingUp, KanbanSquare, Layers, Trophy,
  AlertTriangle, CalendarClock, PauseCircle, UserX, ChevronRight, ArrowLeft, Home,
  UserPlus, XCircle, Percent, Info, SlidersHorizontal,
} from "@/components/icons";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const now = new Date();
const PERIODOS = Array.from({ length: 6 }).map((_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
  return { v: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${MESES[d.getMonth()]} ${d.getFullYear()}` };
});

const achTone = (a) => (a >= 100 ? "text-ok" : a >= 70 ? "text-warn" : "text-bad");

function Kpi({ icon: Icon, label, value, sub, tone = "navy", onClick, testid }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp onClick={onClick} data-testid={testid}
      className={`text-left bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)] ${onClick ? "hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgb(0,0,0,0.07)] transition-all" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`w-4 h-4 ${tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : tone === "bad" ? "text-bad" : tone === "brand" ? "text-brand" : "text-navy"}`} />
      </div>
      <div className={`text-2xl font-bold mt-2 ${tone === "ok" ? "text-ok" : tone === "bad" ? "text-bad" : tone === "brand" ? "text-brand" : "text-navy"}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Comp>
  );
}

export default function Operacao() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [period, setPeriod] = useState(sp.get("period") || PERIODOS[0].v);
  const dir = sp.get("diretor_id") || "";
  const mgr = sp.get("manager_id") || "";
  const vend = sp.get("owner_id") || "";
  const [data, setData] = useState(null);
  const [conquista, setConquista] = useState(null);
  const [sankhyaPainel, setSankhyaPainel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isDG = user?.role === "diretor_geral";

  const syncURL = (next) => {
    const params = new URLSearchParams(sp);
    params.set("period", next.period ?? period);
    ["diretor_id", "manager_id", "owner_id"].forEach((k) => {
      if (k in next) { if (next[k]) params.set(k, next[k]); else params.delete(k); }
    });
    setSp(params, { replace: false });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const p = new URLSearchParams({ period });
      if (dir) p.set("diretor_id", dir);
      if (mgr) p.set("manager_id", mgr);
      if (vend) p.set("owner_id", vend);
      const focoId = vend || mgr || dir || "";
      const cq = new URLSearchParams();
      if (focoId) cq.set("owner_id", focoId);
      const [{ data: d1 }, { data: d2 }] = await Promise.all([
        api.get(`/operacao?${p.toString()}`),
        api.get(`/painel/conquista${cq.toString() ? `?${cq.toString()}` : ""}`),
      ]);
      setData(d1); setConquista(d2);
      // Lote 3 CRM-003: no painel do Diretor Geral, o "Faturamento realizado"
      // vem da MESMA rota que a tela Faturamento (Sankhya), em vez de somar
      // db.quotas_history (que fica em R$ 0,00). A rota do Sankhya não é
      // alterada — só lida pelo front.
      if (isDG) {
        try {
          const r = await api.get("/sankhya/faturamento/painel?periodo=mes_atual");
          setSankhyaPainel(r.data || null);
        } catch { /* mantém R$ 0 se Sankhya indisponível */ }
      }
    } catch { setError(true); } finally { setLoading(false); }
  }, [period, dir, mgr, vend, isDG]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Loading />;
  if (error) return <ErrorState onRetry={load} />;

  const k = data.kpis;
  const gerentes = data.desempenho_gerentes || [];
  const vendedores = data.desempenho_vendedores || [];
  const crumbs = data.crumbs || {};
  const diretoresOpts = data.filtros.diretores || [];
  const gerentesOpts = (data.filtros.gerentes || []).filter((g) => !dir || g.diretor_id === dir);
  const vendOpts = (data.filtros.vendedores || []).filter((v) => (!dir || v.diretor_id === dir || (mgr && v.gerente_id === mgr)) && (!mgr || v.gerente_id === mgr));

  // Lote 3 CRM-003: no painel do DG, Realizado dos vendedores vem do Sankhya
  // (mesma rota que a tela Faturamento). Gerentes NÃO têm valor equivalente
  // nesse endpoint (seria só venda direta, não a equipe), por isso a célula
  // vira "Ver faturamento" linkada para a tela Faturamento.
  const sankhyaByUser = (() => {
    if (!isDG || !sankhyaPainel || !Array.isArray(sankhyaPainel.por_pessoa)) return null;
    const m = {};
    for (const p of sankhyaPainel.por_pessoa) {
      m[p.user_id] = Number(p.realizado || 0);
    }
    return m;
  })();

  const setDiretor = (id) => syncURL({ diretor_id: id, manager_id: "", owner_id: "" });
  const setGerente = (id) => syncURL({ manager_id: id, owner_id: "" });
  const setVendedor = (id) => syncURL({ owner_id: id });
  const voltar = () => {
    if (vend) setVendedor("");
    else if (mgr) setGerente("");
    else if (dir) setDiretor("");
    else navigate(-1);
  };

  const nivelAtual = vend ? "vendedor" : mgr ? "gerente" : dir ? "diretor" : "empresa";

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3 flex-wrap" data-testid="op-crumbs">
        <button onClick={() => syncURL({ diretor_id: "", manager_id: "", owner_id: "" })} className="flex items-center gap-1 hover:text-navy" data-testid="crumb-empresa"><Home className="w-3.5 h-3.5" /> Empresa</button>
        {crumbs.diretor ? <span className="flex items-center gap-1"><ChevronRight className="w-3.5 h-3.5" /><button onClick={() => syncURL({ manager_id: "", owner_id: "" })} className="hover:text-navy" data-testid="crumb-diretor">{crumbs.diretor.name}</button></span> : null}
        {crumbs.gerente ? <span className="flex items-center gap-1"><ChevronRight className="w-3.5 h-3.5" /><button onClick={() => syncURL({ owner_id: "" })} className="hover:text-navy" data-testid="crumb-gerente">{crumbs.gerente.name}</button></span> : null}
        {crumbs.vendedor ? <span className="flex items-center gap-1"><ChevronRight className="w-3.5 h-3.5" /><span className="text-navy font-medium" data-testid="crumb-vendedor">{crumbs.vendedor.name}</span></span> : null}
      </div>

      <PageHeader
        title={crumbs.vendedor?.name || crumbs.gerente?.name || crumbs.diretor?.name || `Olá, ${user?.name?.split(" ")[0]}`}
        subtitle={`Painel Estratégico da Operação · ${data.periodo}`}
        action={
          (dir || mgr || vend) ? (
            <button onClick={voltar} data-testid="op-voltar" className="flex items-center gap-1.5 h-10 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50"><ArrowLeft className="w-4 h-4" /> Voltar</button>
          ) : null
        }
      />

      {!dir && !mgr && !vend && <CotaPendenteBanner compact />}

      {/* Item 3.13 — filtros recolhidos atrás de "Filtros" no mobile; sempre visíveis a partir de md. */}
      <details className="mb-5 md:open:mb-5 md:mb-5 group" data-testid="op-filtros-details">
        <summary className="md:hidden list-none flex items-center gap-2 h-11 px-3 rounded-lg border border-input bg-white text-sm font-medium text-navy cursor-pointer hover:bg-slate-50" data-testid="op-filtros-summary">
          <SlidersHorizontal className="w-4 h-4" />
          <span className="flex-1">Filtros{(dir || mgr || vend) ? " · aplicados" : ""}</span>
          <ChevronRight className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-90" />
        </summary>
        <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
          <select value={period} onChange={(e) => { setPeriod(e.target.value); syncURL({ period: e.target.value }); }} data-testid="op-periodo"
            className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
            {PERIODOS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
          {diretoresOpts.length > 0 && (
            <select value={dir} onChange={(e) => setDiretor(e.target.value)} data-testid="op-diretor"
              className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
              <option value="">Todos os diretores</option>
              {diretoresOpts.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
            </select>
          )}
          <select value={mgr} onChange={(e) => setGerente(e.target.value)} data-testid="op-gerente"
            className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">Todos os gerentes</option>
            {gerentesOpts.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
          </select>
          <select value={vend} onChange={(e) => setVendedor(e.target.value)} data-testid="op-vendedor"
            className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">Todos os vendedores</option>
            {vendOpts.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
          </select>
          {(dir || mgr || vend) && <button onClick={() => syncURL({ diretor_id: "", manager_id: "", owner_id: "" })} className="h-10 px-3 rounded-lg text-sm text-brand hover:bg-brand-soft/50" data-testid="op-limpar">Limpar filtros</button>}
        </div>
      </details>

      {/* Indicadores de conquista (Mensagem 3) — foco principal */}
      {conquista ? (
        <>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Indicadores de conquista</div>
          {(() => {
            // Escopo do card = filtro atual (vend > mgr > dir > usuário logado).
            // Cada KPI abre a lista já filtrada; nunca a base inteira.
            const focoId = vend || mgr || dir || user?.id || "";
            const scopeQP = focoId ? `scope_id=${focoId}` : "";
            const mesAtual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
            const goList = (path) => navigate(path);
            const conquistaGrid = (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="conquista-kpis">
                <Kpi icon={UserPlus} label="Prospects novos" value={num(conquista.prospects_novos.count)} tone="brand"
                  onClick={() => goList(`/clientes?${scopeQP}${scopeQP ? "&" : ""}criado_mes=${mesAtual}`)} testid="kpi-prospects-novos" />
                <Kpi icon={KanbanSquare} label="Pipeline aberto" value={num(conquista.pipeline_total.count)} sub={brl(conquista.pipeline_total.valor)}
                  onClick={() => goList(`/pipeline?${scopeQP}${scopeQP ? "&" : ""}apenas_ativas=1`)} testid="kpi-pipeline-aberto" />
                <Kpi icon={PauseCircle} label="Parados 7d+" value={num(conquista.parados_7d.count)} sub={brl(conquista.parados_7d.valor)} tone={conquista.parados_7d.count > 0 ? "warn" : "navy"}
                  onClick={() => goList(`/foco/paradas?dias=7${focoId ? `&owner_id=${focoId}` : ""}`)} testid="kpi-parados-7d" />
                <Kpi icon={Trophy} label="Ganhos no mês" value={num(conquista.ganhos.count)} sub={brl(conquista.ganhos.valor)} tone="ok"
                  onClick={() => goList(`/pipeline?${scopeQP}${scopeQP ? "&" : ""}fechado_mes=${mesAtual}`)} testid="kpi-ganhos-conquista" />
                <Kpi icon={XCircle} label="Perdidos no mês" value={num(conquista.perdidos.count)} sub={brl(conquista.perdidos.valor)} tone="bad"
                  onClick={() => goList(`/pipeline?${scopeQP}${scopeQP ? "&" : ""}perdido_mes=${mesAtual}`)} testid="kpi-perdidos" />
                <Kpi icon={Percent} label="Taxa de conversão" value={pct(conquista.taxa_conversao, 1)} tone={conquista.taxa_conversao >= 30 ? "ok" : "navy"} testid="kpi-conversao" />
              </div>
            );
            return conquistaGrid;
          })()}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)]">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Pipeline por etapa</div>
              {conquista.pipeline_total.count === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Sem oportunidades ativas no funil.</p>
              ) : (
                <div className="space-y-2" data-testid="pipeline-por-etapa">
                  {conquista.pipeline_por_etapa.map((s) => {
                    const max = Math.max(...conquista.pipeline_por_etapa.map((x) => x.count)) || 1;
                    return (
                      <div key={s.stage} className="flex items-center gap-3 text-sm">
                        <span className="w-28 shrink-0 text-navy">{s.stage}</span>
                        <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full bg-brand" style={{ width: `${(s.count / max) * 100}%` }} />
                        </div>
                        <span className="w-8 text-right font-semibold text-navy">{s.count}</span>
                        <span className="w-24 text-right text-muted-foreground">{brl(s.valor)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid="perdas-por-motivo">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Perdas por motivo</div>
              {conquista.perdidos.por_motivo.length ? (
                <div className="flex flex-wrap gap-2">
                  {conquista.perdidos.por_motivo.map((m) => (
                    <span key={m.motivo} className="text-xs bg-slate-100 rounded-full px-3 py-1.5 text-slate-700">
                      {m.motivo} <span className="font-semibold text-navy">· {m.count}</span> <span className="text-slate-500">· {brl(m.valor)}</span>
                    </span>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground py-6 text-center">Sem perdas no período.</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid="cross-selling-bloco">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Ampliação de mix aberta</div>
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-2xl font-bold text-navy">{num(conquista.cross_selling.abertas_count)}</div>
                  <div className="text-xs text-muted-foreground">{brl(conquista.cross_selling.abertas_valor)} em aberto</div>
                </div>
                <div className="border-l border-black/5 pl-4">
                  <div className="text-sm text-ok font-semibold">{num(conquista.cross_selling.ganhos_count)} ganhos</div>
                  <div className="text-xs text-muted-foreground">{brl(conquista.cross_selling.ganhos_valor)}</div>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid="segmento-bloco">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Distribuição por segmento</div>
              {conquista.distribuicao_segmento.items.length ? (
                <div className="flex flex-wrap gap-2">
                  {conquista.distribuicao_segmento.items.map((s) => (
                    <span key={s.segmento} className="text-xs bg-slate-100 rounded-full px-3 py-1.5 text-slate-700">
                      {s.segmento} <span className="font-semibold text-navy">· {s.count}</span>
                    </span>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground py-4 text-center">Sem clientes no escopo.</p>}
            </div>
          </div>
        </>
      ) : null}

      {/* Meta/Faturado — bloco secundário.
          Lote 3 CRM-003: para o Diretor Geral, remover meta e % da empresa
          (não há meta da empresa); exibir "Faturamento realizado" lendo a
          mesma rota do Sankhya que a tela Faturamento usa, sem alterar
          nenhuma rota /api/sankhya/*. */}
      <details className="mb-6 group" data-testid="bloco-meta-secundario">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none list-none flex items-center gap-2 mb-2 hover:text-navy">
          <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
          {isDG ? "Faturamento realizado" : "Meta e faturamento oficial"}
        </summary>
        {isDG ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Kpi
              icon={DollarSign}
              label="Faturamento realizado (empresa, mês atual)"
              value={brl((sankhyaPainel && sankhyaPainel.empresa && sankhyaPainel.empresa.total_realizado) || 0)}
              sub={sankhyaPainel ? "Fonte: Sankhya · tela Faturamento" : "Carregando do Sankhya…"}
              tone="navy"
              testid="kpi-faturamento"
              onClick={() => navigate("/faturamento")}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Kpi icon={DollarSign} label="Faturamento realizado" value={brl(k.faturamento_realizado)} tone="navy" testid="kpi-faturamento" />
            <Kpi icon={Target} label="Meta" value={brl(k.meta)} tone="navy" testid="kpi-meta" />
            <Kpi icon={TrendingUp} label="% de atingimento" value={pct(k.atingimento)} tone={k.atingimento >= 100 ? "ok" : k.atingimento >= 70 ? "warn" : "bad"} testid="kpi-atingimento" />
          </div>
        )}
      </details>

      {/* Resultado comercial / previsto */}
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Resultado comercial e previsto</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Trophy} label="Oportunidades ganhas" value={num(k.oportunidades_ganhas)} sub={`${brl(k.oportunidades_ganhas_valor)} · ${num(k.oportunidades_perdidas)} perdidas`} tone="ok" testid="kpi-ganhas" />
        <Kpi icon={KanbanSquare} label="Total em oportunidades" value={num(k.total_oportunidades)} sub={brl(k.valor_aberto)} tone="navy" onClick={() => navigate("/pipeline")} testid="kpi-total-op" />
        <Kpi icon={Layers} label="Previsão ponderada" value={brl(k.previsao_ponderada)} tone="brand" testid="kpi-previsao" />
        <Kpi icon={UserX} label="Clientes sem próxima ação" value={num(k.clientes_sem_proxima_acao)} sub={`de ${num(k.total_clientes)} clientes`} tone="bad" onClick={() => navigate("/carteira?filtro=sem_proxima")} testid="kpi-sem-proxima" />
      </div>

      {/* Desvios */}
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Onde a operação precisa de atenção</div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Kpi icon={AlertTriangle} label="Clientes que requerem atenção" value={num(k.clientes_requer_atencao)} tone="warn" onClick={() => navigate("/carteira?filtro=atencao")} testid="kpi-atencao" />
        <Kpi icon={PauseCircle} label="Oportunidades paradas" value={num(k.oportunidades_paradas)} sub="15+ dias sem movimentação" tone="warn" onClick={() => navigate("/atencao")} testid="kpi-paradas" />
        <Kpi icon={CalendarClock} label="Atividades atrasadas" value={num(k.atividades_atrasadas)} tone="bad" onClick={() => navigate("/atividades")} testid="kpi-atrasadas" />
      </div>

      {/* Cards por diretor (visível apenas quando estamos no nível Empresa e há diretores) */}
      {nivelAtual === "empresa" && diretoresOpts.length > 0 && (
        <div className="mb-8" data-testid="op-cards-diretores">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Diretores <span className="text-slate-400 font-normal">({diretoresOpts.length})</span></span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {diretoresOpts.map((d) => (
              <button key={d.id} onClick={() => setDiretor(d.id)} data-testid={`card-diretor-${d.id}`} className="text-left bg-card rounded-2xl border border-black/5 p-4 hover:border-brand/40 transition-all">
                <div className="flex items-center gap-3">
                  <Avatar name={d.nome} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Diretor</div>
                    <div className="font-semibold text-navy truncate">{d.nome}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Desempenho por gerente */}
      <div className="bg-card rounded-2xl border border-black/5 shadow-[0_2px_10px_rgb(0,0,0,0.03)] overflow-hidden mb-6" data-testid="desempenho-gerentes">
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <span className="font-semibold text-navy">Desempenho por gerente</span>
          <span className="text-xs text-muted-foreground">{gerentes.length === 1 ? "1 gerente" : `${gerentes.length} gerentes`}</span>
        </div>
        {gerentes.length === 0 ? <div className="px-5 py-8 text-center text-sm text-muted-foreground">Sem gerentes no escopo.</div> : (
          <div className="overflow-x-auto pipeline-scroll">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-black/5">
                <th className="px-5 py-3">Gerente</th><th className="px-4 py-3 text-center">Vend.</th>
                <th className="px-4 py-3 text-right">Realizado</th><th className="px-4 py-3 text-right">Meta</th>
                <th className="px-4 py-3 text-right">% Ating.</th><th className="px-4 py-3 text-right">Prev. ponderada</th>
                <th className="px-4 py-3 text-center">Atenção</th><th className="px-4 py-3 text-center">Sem próx.</th>
              </tr></thead>
              <tbody>
                {gerentes.map((g) => (
                  <tr key={g.id} onClick={() => setGerente(g.id)} data-testid={`gerente-row-${g.id}`}
                    className="border-b border-black/5 last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="px-5 py-3"><div className="flex items-center gap-3"><Avatar name={g.nome} src={g.avatar} size={30} /><span className="font-medium text-navy">{g.nome}</span></div></td>
                    <td className="px-4 py-3 text-center">{g.vendedores}</td>
                    <td className="px-4 py-3 text-right font-semibold text-navy">
                      {isDG ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate("/faturamento"); }}
                          className="text-brand hover:underline"
                          data-testid={`gerente-ver-faturamento-${g.id}`}
                        >
                          Ver faturamento
                        </button>
                      ) : brl(g.realizado)}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{brl(g.meta)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${isDG ? "text-muted-foreground" : achTone(g.atingimento)}`}>{isDG ? "—" : pct(g.atingimento)}</td>
                    <td className="px-4 py-3 text-right text-brand font-semibold">{brl(g.previsao_ponderada)}</td>
                    <td className="px-4 py-3 text-center">{g.requer_atencao > 0 ? <span className="bg-warn/15 text-warn rounded-full px-2 py-0.5 text-xs font-semibold">{g.requer_atencao}</span> : "—"}</td>
                    <td className="px-4 py-3 text-center">{g.sem_proxima_acao > 0 ? <span className="bg-bad/15 text-bad rounded-full px-2 py-0.5 text-xs font-semibold">{g.sem_proxima_acao}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Desempenho por vendedor */}
      <div className="bg-card rounded-2xl border border-black/5 shadow-[0_2px_10px_rgb(0,0,0,0.03)] overflow-hidden" data-testid="desempenho-vendedores">
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <span className="font-semibold text-navy">Desempenho por vendedor</span>
          <span className="text-xs text-muted-foreground">{plural(vendedores.length, "vendedor", "vendedores")}</span>
        </div>
        {vendedores.length === 0 ? <div className="px-5 py-8 text-center text-sm text-muted-foreground">Sem vendedores no escopo.</div> : (
          <div className="overflow-x-auto pipeline-scroll">
            <table className="w-full text-sm min-w-[860px]">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-black/5">
                <th className="px-5 py-3">Vendedor</th><th className="px-4 py-3">Gerente</th>
                <th className="px-4 py-3 text-right">Realizado</th><th className="px-4 py-3 text-right">% Ating.</th>
                <th className="px-4 py-3 text-right">Oportun.</th><th className="px-4 py-3 text-right">Prev. ponderada</th>
                <th className="px-4 py-3 text-center">Ganhas</th><th className="px-4 py-3 text-center">Atenção</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {vendedores.map((v) => (
                  <tr key={v.id} onClick={() => setVendedor(v.id)} data-testid={`vendedor-row-${v.id}`}
                    className="border-b border-black/5 last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="px-5 py-3"><div className="flex items-center gap-3"><Avatar name={v.nome} src={v.avatar} size={30} /><span className="font-medium text-navy">{v.nome}</span></div></td>
                    <td className="px-4 py-3 text-muted-foreground">{v.gerente_nome || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-navy">
                      {brl(isDG && sankhyaByUser ? (sankhyaByUser[v.id] ?? 0) : v.realizado)}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${achTone(v.atingimento)}`}>{pct(v.atingimento)}</td>
                    <td className="px-4 py-3 text-right">{v.total_oportunidades}</td>
                    <td className="px-4 py-3 text-right text-brand font-semibold">{brl(v.previsao_ponderada)}</td>
                    <td className="px-4 py-3 text-center text-ok font-semibold">{v.ganhas}</td>
                    <td className="px-4 py-3 text-center">{v.requer_atencao > 0 ? <span className="bg-warn/15 text-warn rounded-full px-2 py-0.5 text-xs font-semibold">{v.requer_atencao}</span> : "—"}</td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
